/**
 * MSapling Frontend Logger
 * Structured logging with production safety.
 *
 * - In development: all levels pass through to console.*
 * - In production: debug() and info() are suppressed; warn/error sanitize
 *   arguments to strip values that look like tokens, keys, or secrets.
 */

const IS_PROD = typeof process !== 'undefined'
  && process.env?.NODE_ENV === 'production';

/**
 * Patterns that indicate a value is likely a secret (JWT, API key, bearer
 * token, etc.).  Applied only in production to avoid leaking credentials
 * through browser dev-tools or any log-drain attached to console.
 */
const SECRET_PATTERNS: RegExp[] = [
  /^ey[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/,   // JWT (eyJ...)
  /^sk-[A-Za-z0-9]{20,}/,                           // OpenAI-style key
  /^Bearer\s+\S{20,}/i,                             // Authorization header
  /^ghp_[A-Za-z0-9]{20,}/,                          // GitHub PAT
  /^xox[bpras]-[A-Za-z0-9-]{20,}/,                  // Slack token
  /^AKIA[A-Z0-9]{12,}/,                             // AWS access key
];

const REDACTED = '[REDACTED]';

const looksLikeSecret = (value: string): boolean =>
  SECRET_PATTERNS.some((re) => re.test(value));

/**
 * Recursively walk log arguments and redact string values that match known
 * secret patterns.  Handles plain strings, arrays, and plain objects (one
 * level deep for objects to keep it cheap).
 */
const sanitizeArg = (arg: unknown): unknown => {
  if (typeof arg === 'string') {
    return looksLikeSecret(arg) ? REDACTED : arg;
  }
  if (arg instanceof Error) {
    // Preserve error shape but sanitize the message.
    const cleaned = new Error(
      looksLikeSecret(arg.message) ? REDACTED : arg.message,
    );
    cleaned.name = arg.name;
    cleaned.stack = arg.stack;
    return cleaned;
  }
  if (Array.isArray(arg)) {
    return arg.map(sanitizeArg);
  }
  if (arg !== null && typeof arg === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(arg as Record<string, unknown>)) {
      out[k] = typeof v === 'string' && looksLikeSecret(v) ? REDACTED : v;
    }
    return out;
  }
  return arg;
};

const sanitizeArgs = (args: unknown[]): unknown[] =>
  IS_PROD ? args.map(sanitizeArg) : args;

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (..._args: unknown[]): void => {};

export const logger = {
  /** Suppressed in production. */
  debug: IS_PROD
    ? noop
    : (...args: unknown[]): void => { console.debug('[MSapling]', ...args); },

  /** Suppressed in production. */
  info: IS_PROD
    ? noop
    : (...args: unknown[]): void => { console.log('[MSapling]', ...args); },

  /** Always emitted; sanitized in production. */
  warn: (...args: unknown[]): void => {
    console.warn('[MSapling]', ...sanitizeArgs(args));
  },

  /** Always emitted; sanitized in production. */
  error: (...args: unknown[]): void => {
    console.error('[MSapling]', ...sanitizeArgs(args));
  },
};

/**
 * Legacy Logger kept for backward compatibility with existing call-sites
 * that use Logger.info(feature, msg, data) or Logger.error(feature, code, msg, data).
 */
export const Logger = {
  info: (feature: string, msg: string, data: unknown = null): void => {
    logger.info(`[${feature}]`, msg, data ?? '');
  },
  error: (feature: string, code: string | number, msg: string, data: unknown = null): void => {
    logger.error(`[${feature}] (${code})`, msg, data ?? '');
  },
};
